import { useState } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import ClaimOptionCard from '@/james-playground/aa-family-claim/ClaimOptionCard';
import { say } from '@/james-playground/aa-family-claim/claim/asks';
import {
  MANAGES,
  NEEDED_HINT,
  NIGHT,
} from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  CardShell,
  gateQuestion,
  Chips,
  Count,
  optionsOf,
  Toggle,
  useQids,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';
import {
  initialNight,
  known,
  nightReady,
  nightValues,
  str,
  type NightCells,
} from '@/james-playground/aa-family-claim/claim/cards/values';

/**
 * The help she needs at night, one checkable card per kind the form lists.
 * Ticking one opens how many times a night (the form's own once, twice, 3 or
 * more) and how many minutes each time.
 */
const NightGridCard = ({
  ask,
  facts,
  voice: v,
  onSubmit,
  onSkip,
}: CardProps) => {
  const gateId = ask.gate ?? '';
  const [gate, setGate] = useState(str(known(facts, gateId)));
  const [cells, setCells] = useState<NightCells>(() =>
    initialNight(ask, facts),
  );
  const qid = useQids();
  const rows = ask.nightRows ?? [];

  const update = (key: string, patch: Partial<NightCells[string]>) =>
    setCells((c) => ({
      ...c,
      [key]: { ...(c[key] ?? { on: false, times: '', minutes: '' }), ...patch },
    }));

  const submit = () => {
    const values = nightValues(ask, gate, cells);
    if (gate !== 'Yes') return onSubmit(values, say(MANAGES, v));
    const lines = rows
      .filter((row) => cells[row.times]?.on)
      .map((row) => {
        const cell = cells[row.times];
        return `${say(row.label, v)}: ${NIGHT.timesReply[cell.times] ?? cell.times}, about ${cell.minutes} minutes each time`;
      });
    onSubmit(values, lines.join('. '));
  };

  return (
    <CardShell
      label={say(ask.title, v)}
      ready={nightReady(ask, gate, cells)}
      onContinue={submit}
      onSkip={onSkip}
    >
      <Toggle
        {...gateQuestion(ask, v)}
        hint={say(NEEDED_HINT, v)}
        value={gate}
        onChange={setGate}
      />
      {gate === 'Yes' && (
        <Stack spacing={1.5}>
          <Typography id={qid('pick')} variant='body2'>
            {say(NIGHT.pick, v)}
          </Typography>
          <Stack spacing={1.25} role='group' aria-labelledby={qid('pick')}>
            {rows.map((row) => {
              const cell = cells[row.times];
              const label = say(row.label, v);
              return (
                <ClaimOptionCard
                  key={row.times}
                  control='check'
                  selected={Boolean(cell?.on)}
                  label={label}
                  onClick={() => update(row.times, { on: !cell?.on })}
                >
                  {cell?.on && (
                    // Taps inside the details belong to them, so they must not
                    // reach the card and untick it.
                    <Box
                      onClick={(e) => e.stopPropagation()}
                      sx={{ pt: 1, cursor: 'auto' }}
                    >
                      <Stack spacing={1.5}>
                        <Stack spacing={0.75}>
                          <Typography
                            id={qid(`${row.times}-times`)}
                            variant='body2'
                          >
                            {NIGHT.times}
                          </Typography>
                          <Chips
                            labelledBy={qid(`${row.times}-times`)}
                            options={optionsOf(row.times).map((o) => ({
                              value: o,
                              label: NIGHT.timesOption[o] ?? o,
                            }))}
                            selected={cell.times ? [cell.times] : []}
                            onPick={(times) => update(row.times, { times })}
                          />
                        </Stack>
                        <Stack spacing={0.75}>
                          <Typography variant='body2'>
                            {NIGHT.minutes}
                          </Typography>
                          <Count
                            label={`${label}: ${NIGHT.minutes.toLowerCase()}`}
                            unit={NIGHT.minutesUnit}
                            value={cell.minutes}
                            onChange={(minutes) =>
                              update(row.times, { minutes })
                            }
                            min={1}
                            max={240}
                            step={5}
                            start={5}
                          />
                        </Stack>
                      </Stack>
                    </Box>
                  )}
                </ClaimOptionCard>
              );
            })}
          </Stack>
        </Stack>
      )}
    </CardShell>
  );
};

NightGridCard.displayName = 'NightGridCard';
export default NightGridCard;
